import React, { useEffect, useMemo, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import styled from 'styled-components';
import {
  StatStrip,
  StatCell,
  StatLabel,
  StatValue,
  StatSub,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  Loading,
  Empty,
  portcoTokens,
} from '../portco/PortcoTheme';

import { DateField } from '../shared/DateField';

/**
 * Subscription-line tab — surfaced inside LPFundPerformanceDetailScreen
 * alongside Overview / Cash flows / Benchmark / Secondary / Reports.
 *
 * Backend:
 *   GET  /api/sub-lines?fundId={fundId}
 *   GET  /api/sub-lines/{lineId}/draws
 *   POST /api/sub-lines/{lineId}/draws
 *   POST /api/sub-lines/{lineId}/repayments
 *
 * If no facility exists for the fund, surface an empty state — sub-lines
 * are optional infrastructure that not every fund carries.
 */

interface SubscriptionLine {
  id: number;
  fundId: number;
  facilityName: string;
  lenderName?: string;
  facilitySize: number;
  currency?: string;
  rateIndex?: string;
  spreadBps?: number;
  fixedRate?: number;
  dayCount?: string;
  unusedFeeBps?: number;
  startDate?: string;
  maturityDate?: string;
  status?: string;
  notes?: string;
}

interface SubLineDraw {
  id: number;
  lineId: number;
  drawDate?: string;
  drawAmount: number;
  drawPurpose?: string;
  currency?: string;
  outstandingAmount: number;
  accruedInterest?: number;
  paidInterest?: number;
  status?: string;
  lastRepayDate?: string;
  notes?: string;
}

const fmtMoney = (v?: number) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};
const fmtPct = (v?: number, decimals = 2) =>
  v == null ? '—' : `${v.toFixed(decimals)}%`;
const fmtDate = (iso?: string) => (iso ? iso.substring(0, 10) : '—');
const daysUntil = (iso?: string): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

const StatStrip5 = styled(StatStrip)`
  grid-template-columns: repeat(5, 1fr);
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 0 0 0;
`;

const PillBtn = styled.button`
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
  background: ${portcoTokens.surfaceBody};
  border: 1px solid ${portcoTokens.hairlineStrong};
  color: ${portcoTokens.inkPrimary};
  padding: 7px 14px;
  cursor: pointer;
  &:hover { border-color: ${portcoTokens.accent}; color: ${portcoTokens.accent}; }
  &:focus-visible { outline: 2px solid ${portcoTokens.accent}; outline-offset: 1px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const StatusPill = styled.span<{ $status?: string }>`
  display: inline-block;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 8px;
  border: 1px solid ${portcoTokens.hairline};
  font-weight: 600;
  color: ${(p) =>
    p.$status === 'OUTSTANDING' || p.$status === 'ACTIVE' ? portcoTokens.statusWarning
      : p.$status === 'REPAID' || p.$status === 'MATURED' ? portcoTokens.statusPassing
      : portcoTokens.inkSubtle};
  background: ${(p) =>
    p.$status === 'OUTSTANDING' || p.$status === 'ACTIVE' ? portcoTokens.statusWarningSubtle
      : p.$status === 'REPAID' || p.$status === 'MATURED' ? portcoTokens.statusPassingSubtle
      : portcoTokens.surfaceMuted};
`;

const ModalBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(11, 30, 58, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: ${portcoTokens.surfaceBody};
  border: 1px solid ${portcoTokens.hairlineStrong};
  width: 480px;
  max-width: calc(100vw - 48px);
  padding: 0;
  box-shadow: 0 24px 48px rgba(0,0,0,0.18);
`;

const ModalHead = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${portcoTokens.hairline};
  font-size: 14px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  letter-spacing: 0.01em;
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex; flex-direction: column; gap: 14px;
`;

const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  font-weight: 600;
`;

const TextInput = styled.input`
  font-family: ${portcoTokens.fontMono};
  font-size: 13px;
  background: ${portcoTokens.surfaceBody};
  border: 1px solid ${portcoTokens.hairline};
  color: ${portcoTokens.inkBody};
  padding: 8px 10px;
  &:focus { outline: 2px solid ${portcoTokens.accent}; outline-offset: 1px; }
`;

const SelectInput = styled.select`
  font-family: inherit;
  font-size: 13px;
  background: ${portcoTokens.surfaceBody};
  border: 1px solid ${portcoTokens.hairline};
  color: ${portcoTokens.inkBody};
  padding: 8px 10px;
  &:focus { outline: 2px solid ${portcoTokens.accent}; outline-offset: 1px; }
`;

const ModalFoot = styled.div`
  padding: 14px 20px;
  border-top: 1px solid ${portcoTokens.hairline};
  display: flex; justify-content: flex-end; gap: 8px;
`;

const ErrorBanner = styled.div`
  background: ${portcoTokens.statusBreachedSubtle};
  border: 1px solid ${portcoTokens.statusBreached};
  color: ${portcoTokens.statusBreached};
  padding: 10px 14px;
  font-size: 12px;
  margin-bottom: 16px;
`;

interface SubscriptionLineTabProps {
  fundId: number;
}

type ModalMode = null | 'draw' | 'repay';

const SubscriptionLineTab: React.FC<SubscriptionLineTabProps> = ({ fundId }) => {
  const [lines, setLines] = useState<SubscriptionLine[]>([]);
  const [draws, setDraws] = useState<SubLineDraw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [modal, setModal] = useState<ModalMode>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form state shared by both modals
  const [amount, setAmount] = useState('');
  const [actionDate, setActionDate] = useState<string>(getTodayDate());
  const [purpose, setPurpose] = useState<'INVESTMENT' | 'EXPENSES' | 'OTHER'>('INVESTMENT');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get<SubscriptionLine[]>(`/api/sub-lines?fundId=${fundId}`);
        const rows = Array.isArray(r.data) ? r.data : [];
        if (cancelled) return;
        setLines(rows);
        if (rows.length === 0) {
          setDraws([]);
        } else {
          // Fetch draws for each active line (typically 1 facility per fund)
          const drawArrays = await Promise.all(
            rows.map((l) => axios.get<SubLineDraw[]>(`/api/sub-lines/${l.id}/draws`).then((r) => r.data).catch(() => []))
          );
          if (cancelled) return;
          setDraws(drawArrays.flat());
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load sub-line data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fundId, reloadKey]);

  const primaryLine = lines[0]; // demo + UX assumption: one facility per fund

  const totals = useMemo(() => {
    const out = draws.reduce((sum, d) => sum + (d.outstandingAmount || 0), 0);
    const acc = draws.reduce((sum, d) => sum + (d.accruedInterest || 0), 0);
    const size = primaryLine?.facilitySize || 0;
    const avail = Math.max(size - out, 0);
    const utilPct = size > 0 ? (out / size) * 100 : 0;
    const dtm = daysUntil(primaryLine?.maturityDate);
    return { outstanding: out, accrued: acc, size, available: avail, utilPct, daysToMaturity: dtm };
  }, [draws, primaryLine]);

  const allInRate = useMemo(() => {
    if (!primaryLine) return null;
    if (primaryLine.fixedRate != null) return primaryLine.fixedRate * 100;
    // SOFR proxy 5.30% + spread (bps → %) — display-only; server-side authoritative.
    const benchmark = 5.30;
    const spread = (primaryLine.spreadBps ?? 0) / 100;
    return benchmark + spread;
  }, [primaryLine]);

  const openDraw = () => {
    setAmount(''); setPurpose('INVESTMENT');
    setActionDate(getTodayDate());
    setSubmitError(null);
    setModal('draw');
  };
  const openRepay = () => {
    setAmount('');
    setActionDate(getTodayDate());
    setSubmitError(null);
    setModal('repay');
  };
  const closeModal = () => { if (!submitting) setModal(null); };

  const submitDraw = async () => {
    if (!primaryLine) return;
    const amt = Number(amount);
    if (!amt || amt <= 0) { setSubmitError('Enter a positive draw amount.'); return; }
    if (amt > totals.available) { setSubmitError(`Exceeds available capacity (${fmtMoney(totals.available)}).`); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      await axios.post(`/api/sub-lines/${primaryLine.id}/draws`, {
        amount: amt, drawDate: actionDate, purpose,
      });
      setModal(null);
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      setSubmitError(e?.response?.data?.message || e?.message || 'Draw failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRepay = async () => {
    if (!primaryLine) return;
    const amt = Number(amount);
    if (!amt || amt <= 0) { setSubmitError('Enter a positive repayment amount.'); return; }
    if (amt > totals.outstanding) { setSubmitError(`Exceeds outstanding (${fmtMoney(totals.outstanding)}).`); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      await axios.post(`/api/sub-lines/${primaryLine.id}/repayments`, {
        amount: amt, repayDate: actionDate,
      });
      setModal(null);
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      setSubmitError(e?.response?.data?.message || e?.message || 'Repayment failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading>Loading subscription-line facility…</Loading>;

  if (error) return <ErrorBanner>{error}</ErrorBanner>;

  if (!primaryLine) {
    return (
      <Panel>
        <PanelHead>
          <PanelTitle>Subscription Line</PanelTitle>
          <PanelMeta>No facility on file</PanelMeta>
        </PanelHead>
        <PanelBody>
          <Empty>
            This fund has no subscription-line facility configured. To onboard one,
            POST a facility record to <code>/api/sub-lines</code> with the fund and
            lender details.
          </Empty>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <>
      <StatStrip5>
        <StatCell>
          <StatLabel>Facility size</StatLabel>
          <StatValue>{fmtMoney(totals.size)}</StatValue>
          <StatSub>{primaryLine.lenderName || '—'} · {primaryLine.currency || 'USD'}</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Drawn</StatLabel>
          <StatValue>{fmtMoney(totals.outstanding)}</StatValue>
          <StatSub>{fmtPct(totals.utilPct, 1)} utilization</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Available</StatLabel>
          <StatValue>{fmtMoney(totals.available)}</StatValue>
          <StatSub>uncalled commitments back the line</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Accrued interest</StatLabel>
          <StatValue>{fmtMoney(totals.accrued)}</StatValue>
          <StatSub>all-in {fmtPct(allInRate ?? undefined, 2)}</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Days to maturity</StatLabel>
          <StatValue>{totals.daysToMaturity != null ? totals.daysToMaturity.toLocaleString() : '—'}</StatValue>
          <StatSub>matures {fmtDate(primaryLine.maturityDate)}</StatSub>
        </StatCell>
      </StatStrip5>

      <Panel>
        <PanelHead>
          <PanelTitle>{primaryLine.facilityName}</PanelTitle>
          <PanelMeta>
            {primaryLine.rateIndex || 'FIXED'}
            {primaryLine.spreadBps != null ? ` + ${primaryLine.spreadBps}bps` : ''}
            {' · '}
            {primaryLine.dayCount || 'ACT_360'}
            {primaryLine.unusedFeeBps != null ? ` · ${primaryLine.unusedFeeBps}bps unused fee` : ''}
          </PanelMeta>
        </PanelHead>
        <PanelBody>
          <Toolbar>
            <PillBtn onClick={openDraw} disabled={totals.available <= 0}>Draw</PillBtn>
            <PillBtn onClick={openRepay} disabled={totals.outstanding <= 0}>Repay</PillBtn>
            <span style={{ flex: 1 }} />
            <StatusPill $status={primaryLine.status}>{primaryLine.status || '—'}</StatusPill>
          </Toolbar>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead>
          <PanelTitle>Draws</PanelTitle>
          <PanelMeta>{draws.length} on file</PanelMeta>
        </PanelHead>
        <PanelBody style={{ padding: 0 }}>
          {draws.length === 0 ? (
            <Empty>No draws against this facility yet.</Empty>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Draw date</Th>
                  <Th style={{ textAlign: 'right' }}>Draw amount</Th>
                  <Th>Purpose</Th>
                  <Th style={{ textAlign: 'right' }}>Outstanding</Th>
                  <Th style={{ textAlign: 'right' }}>Accrued</Th>
                  <Th>Status</Th>
                  <Th>Last repay</Th>
                </tr>
              </Thead>
              <tbody>
                {draws.map((d) => (
                  <Tr key={d.id}>
                    <TdMono>{fmtDate(d.drawDate)}</TdMono>
                    <TdMono style={{ textAlign: 'right' }}>{fmtMoney(d.drawAmount)}</TdMono>
                    <Td>{d.drawPurpose || '—'}</Td>
                    <TdMono style={{ textAlign: 'right' }}>{fmtMoney(d.outstandingAmount)}</TdMono>
                    <TdMono style={{ textAlign: 'right' }}>{fmtMoney(d.accruedInterest)}</TdMono>
                    <Td><StatusPill $status={d.status}>{d.status || '—'}</StatusPill></Td>
                    <TdMono>{fmtDate(d.lastRepayDate)}</TdMono>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </PanelBody>
      </Panel>

      {modal === 'draw' && (
        <ModalBackdrop onClick={closeModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHead>Draw against {primaryLine.facilityName}</ModalHead>
            <ModalBody>
              {submitError && <ErrorBanner>{submitError}</ErrorBanner>}
              <Field>
                Amount ({primaryLine.currency || 'USD'})
                <TextInput
                  type="number" step="0.01" min="0"
                  value={amount} placeholder={`max ${fmtMoney(totals.available)}`}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field>
                Draw date
                <DateField tone="light" value={actionDate ?? ''} onChange={iso => setActionDate(iso)} />
              </Field>
              <Field>
                Purpose
                <SelectInput value={purpose} onChange={(e) => setPurpose(e.target.value as any)}>
                  <option value="INVESTMENT">Investment</option>
                  <option value="EXPENSES">Expenses</option>
                  <option value="OTHER">Other</option>
                </SelectInput>
              </Field>
            </ModalBody>
            <ModalFoot>
              <PillBtn onClick={closeModal} disabled={submitting}>Cancel</PillBtn>
              <PillBtn onClick={submitDraw} disabled={submitting}>{submitting ? 'Submitting…' : 'Confirm draw'}</PillBtn>
            </ModalFoot>
          </ModalCard>
        </ModalBackdrop>
      )}

      {modal === 'repay' && (
        <ModalBackdrop onClick={closeModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHead>Repay {primaryLine.facilityName}</ModalHead>
            <ModalBody>
              {submitError && <ErrorBanner>{submitError}</ErrorBanner>}
              <Field>
                Repayment amount ({primaryLine.currency || 'USD'})
                <TextInput
                  type="number" step="0.01" min="0"
                  value={amount} placeholder={`outstanding ${fmtMoney(totals.outstanding)}`}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field>
                Repayment date
                <DateField tone="light" value={actionDate ?? ''} onChange={iso => setActionDate(iso)} />
              </Field>
              <div style={{ fontSize: 12, color: portcoTokens.inkSubtle, lineHeight: 1.5 }}>
                Repayments cascade oldest draw first.
              </div>
            </ModalBody>
            <ModalFoot>
              <PillBtn onClick={closeModal} disabled={submitting}>Cancel</PillBtn>
              <PillBtn onClick={submitRepay} disabled={submitting}>{submitting ? 'Submitting…' : 'Confirm repayment'}</PillBtn>
            </ModalFoot>
          </ModalCard>
        </ModalBackdrop>
      )}
    </>
  );
};

export default SubscriptionLineTab;
